import * as Yup from 'yup'
import { phoneRegExp } from '../../helpers'

export const getInitialPartnerValues = () => ({
  partnerId: null,
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  externalPartnerType: null,
  extensionPeriod: '',
  emailNotProvided: false,
  phoneNotProvided: false,
  details: ''
})

export const getPartnerValidationSchema = () => {
  return Yup.object().shape({
    extensionPeriod: Yup.string().when('externalPartnerType', ([externalPartnerType], schema) =>
      externalPartnerType?.label === 'Mandatar'
        ? schema.required('Extension period is required')
        : schema.nullable()
    ),
    firstName: Yup.string().required('First name is required'),
    lastName: Yup.string().required('Last name is required'),
    email: Yup.string()
      .email('Invalid email')
      .when('emailNotProvided', ([emailNotProvided], schema) =>
        emailNotProvided ? schema.nullable() : schema.required('Email is required')
      ),
    phone: Yup.string().when('phoneNotProvided', ([phoneNotProvided], schema) =>
      phoneNotProvided
        ? schema.nullable()
        : schema.matches(phoneRegExp, 'Phone number is invalid').required('Phone is required')
    ),
    externalPartnerType: Yup.object()
      .shape({
        label: Yup.string().required('Partner type is required'),
        value: Yup.string().required('Partner type is required')
      })
      .required('Partner type is required'),
    details: Yup.string().when('externalPartnerType', ([externalPartnerType], schema) =>
      !['Mandatar', 'Vendor', 'Contractor'].includes(externalPartnerType?.label)
        ? schema.required('Details is required')
        : schema.nullable()
    )
  })
}

export const getInitialAssignmentValues = () => ({
  isNewLegalEntity: false,
  legalEntityName: '',
  legalEntityCIF: '',
  legalEntityCountry: '',
  jobTitle: '',
  startDate: '',
  endDate: '',
  adAccount: false,
  googleAccount: false,
  ldapAccount: false,
  manager: null,
  delegate: null,
  store: [],
  updateComment: '',
  assignmentDetails: ''
})

export const getAssignmentValidationSchema = externalPartnerType => {
  return Yup.object()
    .shape({
      legalEntityName: Yup.string().required('Legal entity name is required'),
      legalEntityCIF: Yup.string().required('Legal entity CIF is required'),
      legalEntityCountry: Yup.object()
        .shape({
          label: Yup.string().required('Legal entity country is required.'),
          value: Yup.string().required('Legal entity country is required.')
        })
        .required('Legal entity country is required.'),
      jobTitle: Yup.string().required('Job Title is required'),
      startDate: Yup.date().required('Start Date is required'),
      endDate: Yup.date()
        .nullable()
        .test('is-after-start-date', 'End Date must be after Start Date', function (value) {
          const startDate = this.parent.startDate
          if (!value) return true
          if (value > startDate) return true
          return this.createError({ message: 'End Date must be after Start Date' })
        }),
      updateComment: Yup.string().nullable(),
      adAccount: Yup.boolean(),
      googleAccount: Yup.boolean(),
      ldapAccount: Yup.boolean(),
      manager: Yup.object()
        .shape({
          label: Yup.string().required('Manager is required.'),
          value: Yup.string().required('Manager is required.')
        })
        .required('Manager is required.'),
      delegate: Yup.object()
        .shape({
          label: Yup.string().nullable(),
          value: Yup.string().nullable()
        })
        .nullable(),
      store:
        externalPartnerType?.value === 'Mandatar'
          ? Yup.array()
              .of(
                Yup.object().shape({
                  label: Yup.string().required('Store is required.'),
                  value: Yup.string().required('Store is required.')
                })
              )
              .required('Store is required.')
          : Yup.array().nullable(),
      assignmentDetails: Yup.string().nullable()
    })
    .test(
      'oneOfRequired',
      'adAccount or googleAccount or ldapAccount is required',
      function (value) {
        const { adAccount, googleAccount, ldapAccount } = value
        if (!adAccount && !googleAccount && !ldapAccount) {
          return this.createError({
            path: 'googleAccount',
            message: 'AD Account or LDAP Account or Google Account is required'
          })
        }
        return true
      }
    )
    .test('storeRequired', 'Store is required', function (value) {
      const { store } = value
      if (externalPartnerType && externalPartnerType.label === 'Mandatar' && store.length === 0) {
        return this.createError({ path: 'store', message: 'Store is required' })
      }
      return true
    })
}
